import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  BookOpenText,
  Play,
  Lock,
  CheckCircle,
  ArrowRight,
  FilePdf,
  Sparkle,
  Certificate,
  Circle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MemberPdfLibrary from "@/components/MemberPdfLibrary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const youtubeEmbed = (url) => {
  if (!url) return null;
  const m =
    url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/) ||
    url.match(/^([\w-]{11})$/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  if (url.includes("vimeo.com")) {
    const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
    if (id) return `https://player.vimeo.com/video/${id}`;
  }
  return null;
};

const CourseLearn = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [courseContent, setCourseContent] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeLessonData, setActiveLessonData] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [view, setView] = useState("lessons"); // lessons | pdfs
  const [completed, setCompleted] = useState([]); // list of lesson_ids
  const [progress, setProgress] = useState({ completed_count: 0, total_lessons: 0, is_course_complete: false });
  const [downloadingCert, setDownloadingCert] = useState(false);

  const loadCompletions = async (em) => {
    try {
      const res = await axios.get(`${API}/lessons-completions`, { params: { email: em } });
      setCompleted(res.data.completed || []);
      setProgress({
        completed_count: res.data.completed_count || 0,
        total_lessons: res.data.total_lessons || 0,
        is_course_complete: !!res.data.is_course_complete,
      });
    } catch {
      /* silent */
    }
  };

  const toggleComplete = async (lessonId) => {
    const isDone = completed.includes(lessonId);
    try {
      const endpoint = isDone ? "uncomplete" : "complete";
      await axios.post(`${API}/lessons/${lessonId}/${endpoint}`, { email });
      toast.success(isDone ? "Marked incomplete" : "Nice work! Lesson complete 🌟");
      loadCompletions(email);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update progress");
    }
  };

  const downloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const res = await axios.get(`${API}/course/certificate`, {
        params: { email, name: email.split("@")[0] },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Certificate-of-Completion.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded! 🎉");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Not yet unlocked — finish all lessons first");
    }
    setDownloadingCert(false);
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("course_email");
    if (storedEmail) {
      setEmail(storedEmail);
      checkAccess(storedEmail);
    } else {
      setIsChecking(false);
    }
  }, []);

  const checkAccess = async (emailToCheck) => {
    setIsChecking(true);
    try {
      const response = await axios.post(`${API}/course/access`, { email: emailToCheck });
      if (response.data.has_access) {
        setHasAccess(true);
        localStorage.setItem("course_email", emailToCheck);
        fetchCourseContent();
        loadCompletions(emailToCheck);
      } else {
        setHasAccess(false);
        toast.error("No course access found for this email");
      }
    } catch (error) {
      setHasAccess(false);
    }
    setIsChecking(false);
  };

  const fetchCourseContent = async () => {
    try {
      const response = await axios.get(`${API}/course/content`);
      setCourseContent(response.data);
    } catch (error) {
      console.error("Failed to fetch course content", error);
    }
  };

  const loadLesson = async (lesson) => {
    setActiveLesson(lesson);
    if (lesson.type !== "video") {
      // downloadable item — handle separately
      setActiveLessonData(null);
      return;
    }
    setLoadingLesson(true);
    try {
      const res = await axios.get(`${API}/lessons/${lesson.id}`, { params: { email } });
      setActiveLessonData(res.data);
    } catch {
      setActiveLessonData(null);
    }
    setLoadingLesson(false);
  };

  const handleCheckAccess = (e) => {
    e.preventDefault();
    if (email) checkAccess(email);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-nunito text-muted-foreground">Checking access...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full">
          <div className="bg-white border-2 border-foreground rounded-3xl shadow-brutal p-8">
            <div className="text-center mb-6">
              <Lock size={48} className="text-muted-foreground mx-auto mb-4" />
              <h1 className="font-fredoka font-bold text-2xl text-foreground mb-2">Access Your Course</h1>
              <p className="font-nunito text-muted-foreground">Enter the email you used to purchase the course.</p>
            </div>
            <form onSubmit={handleCheckAccess} className="space-y-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full font-nunito border-2 border-foreground rounded-xl shadow-brutal-sm py-5"
                data-testid="access-email-input"
              />
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-xl py-5"
                data-testid="access-submit-btn"
              >
                Access Course
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="font-nunito text-sm text-muted-foreground mb-2">Don't have access yet?</p>
              <a href="/course" className="font-nunito font-bold text-primary hover:underline">
                Purchase the Course
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center gap-2">
              <BookOpenText size={32} weight="duotone" className="text-primary" />
              <span className="font-fredoka font-bold text-xl text-foreground">Course Dashboard</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="font-nunito text-sm text-muted-foreground hidden sm:inline">{email}</span>
              <Button
                variant="outline"
                onClick={() => {
                  localStorage.removeItem("course_email");
                  setHasAccess(false);
                  setEmail("");
                }}
                className="font-nunito text-sm border-2 border-foreground rounded-full"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* AI Tutor CTA */}
        <motion.a
          href="/course/tutor"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="block mb-6 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 border-2 border-foreground rounded-2xl shadow-brutal p-5 text-white hover:opacity-95 transition-opacity"
          data-testid="course-learn-tutor-cta"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Sparkle size={28} weight="fill" />
              <div>
                <p className="font-fredoka font-bold text-xl">Try your AI Tutor</p>
                <p className="font-nunito text-white/90 text-sm">Personal 1-on-1 vocabulary tutoring</p>
              </div>
            </div>
            <div className="bg-white text-foreground font-fredoka font-bold border-2 border-foreground rounded-full px-5 py-2 shadow-brutal-sm">
              Open <ArrowRight size={16} weight="bold" className="inline ml-1" />
            </div>
          </div>
        </motion.a>

        {/* Progress + Certificate banner */}
        {progress.total_lessons > 0 && (
          <div
            className="mb-6 bg-white border-2 border-foreground rounded-2xl shadow-brutal-sm p-5"
            data-testid="course-progress-banner"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
              <div>
                <p className="font-fredoka font-bold text-foreground">
                  Your Progress:{" "}
                  <span className="text-primary">
                    {progress.completed_count} / {progress.total_lessons}
                  </span>{" "}
                  lessons complete
                </p>
                <p className="font-nunito text-sm text-muted-foreground">
                  {progress.is_course_complete
                    ? "🎉 Course complete! Claim your certificate."
                    : "Mark lessons complete as you finish them."}
                </p>
              </div>
              <Button
                onClick={downloadCertificate}
                disabled={!progress.is_course_complete || downloadingCert}
                className="bg-accent-purple hover:bg-purple-600 text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-full disabled:opacity-50"
                data-testid="download-certificate-btn"
              >
                <Certificate size={18} weight="fill" className="mr-2" />
                {downloadingCert
                  ? "Preparing…"
                  : progress.is_course_complete
                  ? "Download Certificate"
                  : `Certificate locked (${progress.total_lessons - progress.completed_count} left)`}
              </Button>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden border-2 border-foreground">
              <div
                className="h-full bg-gradient-to-r from-primary via-accent-purple to-orange-400 transition-all duration-500"
                style={{
                  width: `${Math.min(100, (progress.completed_count / Math.max(1, progress.total_lessons)) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* View tabs */}
        <div className="flex gap-2 mb-6 border-b-2 border-foreground/10">
          <button
            onClick={() => setView("lessons")}
            className={`px-5 py-3 font-nunito font-bold flex items-center border-b-4 transition-colors ${
              view === "lessons" ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            }`}
            data-testid="course-tab-lessons"
          >
            <BookOpenText size={18} weight="duotone" className="mr-2" />
            Lessons
          </button>
          <button
            onClick={() => setView("pdfs")}
            className={`px-5 py-3 font-nunito font-bold flex items-center border-b-4 transition-colors ${
              view === "pdfs" ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            }`}
            data-testid="course-tab-pdfs"
          >
            <FilePdf size={18} weight="duotone" className="mr-2" />
            PDF Library
          </button>
        </div>

        {view === "pdfs" ? (
          <MemberPdfLibrary email={email} />
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="bg-white border-2 border-foreground rounded-2xl shadow-brutal p-6 sticky top-24">
                <h2 className="font-fredoka font-bold text-xl text-foreground mb-4">Course Modules</h2>
                {courseContent?.modules?.map((module) => (
                  <div key={module.id} className="mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <span className="font-fredoka font-bold text-white text-sm">{module.id}</span>
                      </div>
                      <h3 className="font-nunito font-bold text-foreground text-sm">{module.title}</h3>
                    </div>
                    <div className="ml-11 space-y-1">
                      {module.lessons.map((lesson) => {
                        const isDone = completed.includes(lesson.id);
                        return (
                          <button
                            key={lesson.id}
                            onClick={() => loadLesson(lesson)}
                            className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                              activeLesson?.id === lesson.id
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-muted text-muted-foreground"
                            }`}
                            data-testid={`lesson-btn-${lesson.id}`}
                          >
                            <div className="flex items-center gap-2">
                              {isDone ? (
                                <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0" />
                              ) : lesson.type === "video" ? (
                                <Play size={14} weight="fill" />
                              ) : (
                                <FilePdf size={14} weight="fill" />
                              )}
                              <span className={`font-nunito ${isDone ? "line-through opacity-70" : ""}`}>
                                {lesson.title}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2">
              {!activeLesson ? (
                <div className="bg-white border-2 border-foreground rounded-2xl shadow-brutal p-8 text-center">
                  <CheckCircle size={64} weight="duotone" className="text-primary mx-auto mb-4" />
                  <h1 className="font-fredoka font-bold text-2xl text-foreground mb-4">Welcome to the Course!</h1>
                  <p className="font-nunito text-muted-foreground mb-2">
                    Select a lesson from the sidebar to get started.
                  </p>
                  <p className="font-nunito text-sm text-muted-foreground">You have lifetime access to all materials.</p>
                </div>
              ) : activeLesson.type !== "video" ? (
                <div className="bg-white border-2 border-foreground rounded-2xl shadow-brutal p-8">
                  <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <h1 className="font-fredoka font-bold text-2xl text-foreground">{activeLesson.title}</h1>
                    <LessonCompleteBtn
                      done={completed.includes(activeLesson.id)}
                      onClick={() => toggleComplete(activeLesson.id)}
                    />
                  </div>
                  <p className="font-nunito text-muted-foreground mb-4">
                    This is a downloadable resource. See the PDF Library tab for all worksheets and printables.
                  </p>
                  <Button
                    onClick={() => setView("pdfs")}
                    className="bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal rounded-xl"
                  >
                    <FilePdf size={18} weight="fill" className="mr-2" />
                    Open PDF Library
                  </Button>
                </div>
              ) : (
                <motion.div
                  key={activeLesson.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border-2 border-foreground rounded-2xl shadow-brutal p-8"
                >
                  <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <h1 className="font-fredoka font-bold text-2xl text-foreground">
                      {activeLessonData?.title || activeLesson.title}
                    </h1>
                    <LessonCompleteBtn
                      done={completed.includes(activeLesson.id)}
                      onClick={() => toggleComplete(activeLesson.id)}
                    />
                  </div>

                  {/* Video */}
                  {activeLessonData?.video_url && youtubeEmbed(activeLessonData.video_url) ? (
                    <div className="aspect-video mb-6 rounded-xl overflow-hidden border-2 border-foreground">
                      <iframe
                        src={youtubeEmbed(activeLessonData.video_url)}
                        title={activeLessonData.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-foreground/5 rounded-xl flex items-center justify-center mb-6 border-2 border-dashed border-foreground/20">
                      <div className="text-center">
                        <Play size={48} weight="fill" className="text-muted-foreground mx-auto mb-2" />
                        <p className="font-nunito text-muted-foreground text-sm">
                          Video coming soon • {activeLesson.duration || ""}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Markdown content */}
                  {loadingLesson ? (
                    <div className="text-center py-8">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                  ) : activeLessonData?.content ? (
                    <article className="prose prose-sm sm:prose-base max-w-none font-nunito" data-testid="lesson-content">
                      <ReactMarkdown>{activeLessonData.content}</ReactMarkdown>
                    </article>
                  ) : (
                    <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
                      <p className="font-nunito text-sm text-foreground">
                        ✍️ Lesson content coming soon. The instructor is updating this lesson — check back shortly!
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseLearn;

const LessonCompleteBtn = ({ done, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-foreground font-nunito font-bold text-sm transition-colors ${
      done
        ? "bg-green-100 text-green-700 hover:bg-green-200"
        : "bg-white text-foreground hover:bg-muted"
    }`}
    data-testid="lesson-mark-complete-btn"
    aria-pressed={done}
  >
    {done ? (
      <>
        <CheckCircle size={16} weight="fill" className="text-green-500" />
        Completed
      </>
    ) : (
      <>
        <Circle size={16} weight="regular" />
        Mark Complete
      </>
    )}
  </button>
);
